/**
 * questionImportSpec.js
 * ---------------------------------------------------------------------------
 * Single source of truth for the Excel bulk-import format.
 *
 * Used by:
 *   - ImportQuestionsModal.jsx  (parse + validate + preview + template download)
 *   - bulkAddQuestions.js       (server re-validates using the same rules)
 *
 * Nothing here touches React or Express, so it can be unit-tested directly
 * and shared between the two codebases.
 */

import * as XLSX from 'xlsx';

// ─── Limits ───────────────────────────────────────────────────────────────────
export const MAX_OPTIONS   = 6;     // matches the "Add Manual" form ceiling
export const MIN_OPTIONS   = 4;     // every MCQ must offer at least this many choices
export const MAX_EXAMPLES  = 3;     // coding examples flattened into columns
export const MAX_ROWS      = 500;   // per import, across all three sheets
export const MAX_FIELD_LEN = 8000;  // warn above this; Excel cell cap is 32767
export const MAX_FILE_MB   = 10;

export const DIFFICULTIES      = ['easy', 'medium', 'hard'];
export const CODING_CATEGORIES = ['Coding', 'Conceptual', 'Debugging', 'Tooling'];
export const QUESTION_ANGLES   = [
  'debug/troubleshoot',
  'explain how/why',
  'compare A vs B',
  'what happens when',
  'design/architect',
];

export const TYPES = ['mcq', 'subjective', 'coding'];

export const TYPE_LABEL = {
  mcq:        'MCQ',
  subjective: 'Theory',
  coding:     'Coding',
};

// Sheet-name aliases, normalised. "Theory" is the user-facing word for the
// subjective_questions array, so accept both.
export const SHEET_ALIASES = {
  mcq:        ['mcq', 'mcqs', 'multiplechoice', 'multiplechoicequestions', 'objective'],
  subjective: ['theory', 'subjective', 'theoryquestions', 'subjectivequestions', 'descriptive'],
  coding:     ['coding', 'programming', 'codingquestions', 'programmingquestions', 'code'],
};

// ─── Text normalisation ───────────────────────────────────────────────────────

const SMART_MAP = {
  '\u2018': "'", '\u2019': "'",
  '\u201C': '"', '\u201D': '"',
  '\u2013': '-', '\u2014': '-',
  '\u2026': '...',
};

/**
 * Coerce any cell value to a clean string.
 * Handles: null/undefined, numbers, Dates, non-breaking spaces, smart quotes
 * from Word/Google Docs paste, CRLF line endings, and stray outer whitespace.
 */
export const clean = (v) => {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  let s = typeof v === 'string' ? v : String(v);
  s = s.replace(/\u00A0/g, ' ');
  s = s.replace(/[\u2018\u2019\u201C\u201D\u2013\u2014\u2026]/g, (m) => SMART_MAP[m]);
  s = s.replace(/\r\n?/g, '\n');
  s = s.replace(/[ \t]+\n/g, '\n');
  return s.trim();
};

/** Normalise a header cell so "Correct Answer", "correct_answer" and "CorrectAnswer" all match. */
export const normKey = (k) => String(k ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Normalise a question for duplicate detection: case, punctuation and spacing insensitive. */
export const dupeKey = (s) =>
  clean(s).toLowerCase().replace(/\s+/g, ' ').replace(/[.,;:!?'"()\[\]-]/g, '').trim();

/** Map any casing/spelling of a difficulty onto the canonical value, or '' if unrecognised. */
export const normDifficulty = (v) => {
  const s = clean(v).toLowerCase();
  if (!s) return '';
  if (['easy', 'e', 'low', 'beginner', 'basic'].includes(s)) return 'easy';
  if (['medium', 'med', 'm', 'moderate', 'intermediate', 'average'].includes(s)) return 'medium';
  if (['hard', 'h', 'high', 'difficult', 'advanced', 'expert'].includes(s)) return 'hard';
  return '';
};

/** Map any casing of a coding category onto the canonical value, or '' if unrecognised. */
export const normCategory = (v) => {
  const s = normKey(v);
  if (!s) return '';
  const hit = CODING_CATEGORIES.find((c) => normKey(c) === s);
  if (hit) return hit;
  if (['debug', 'bug', 'bugfix'].includes(s)) return 'Debugging';
  if (['concept', 'theoretical'].includes(s)) return 'Conceptual';
  if (['tools', 'tool'].includes(s)) return 'Tooling';
  if (['program', 'problem', 'dsa'].includes(s)) return 'Coding';
  return '';
};

/**
 * Split a multi-value cell into a list.
 * Accepts newlines (Alt+Enter) or pipes as separators, and strips leading
 * bullets / "1." / "-" that survive a copy-paste from a doc.
 */
export const splitList = (v) =>
  clean(v)
    .split(/\n|\|/)
    .map((s) =>
      s
        .replace(/^[\s\-*\u2022\u00B7]+/, '')
        .replace(/^\d+[.)]\s*/, '')
        .trim()
    )
    .filter(Boolean);

// ─── Column definitions ───────────────────────────────────────────────────────
// `header` is what the template writes. `aliases` are additional accepted
// spellings (already normalised through normKey).

const optionColumns = Array.from({ length: MAX_OPTIONS }, (_, i) => {
  const letter = String.fromCharCode(65 + i);
  return {
    key: `option${letter}`,
    header: `Option ${letter}`,
    aliases: [`option${letter}`, `opt${letter}`, `choice${letter}`, letter.toLowerCase()],
  };
});

const exampleColumns = Array.from({ length: MAX_EXAMPLES }, (_, i) => {
  const n = i + 1;
  return [
    { key: `example${n}_input`,       header: `Example ${n} Input`,       aliases: [`example${n}input`, `input${n}`] },
    { key: `example${n}_output`,      header: `Example ${n} Output`,      aliases: [`example${n}output`, `output${n}`] },
    { key: `example${n}_explanation`, header: `Example ${n} Explanation`, aliases: [`example${n}explanation`, `explanation${n}`] },
  ];
}).flat();

export const COLUMNS = {
  mcq: [
    { key: 'question',       header: 'Question',       required: true, aliases: ['question', 'questiontext', 'q', 'ques'] },
    ...optionColumns,
    { key: 'correct_answer', header: 'Correct Answer', required: true, aliases: ['correctanswer', 'answer', 'correct', 'correctoption', 'correctoptions', 'key', 'ans'] },
    { key: 'difficulty',     header: 'Difficulty',     aliases: ['difficulty', 'level', 'diff'] },
  ],

  subjective: [
    { key: 'question',         header: 'Question',         required: true, aliases: ['question', 'questiontext', 'q', 'ques'] },
    { key: 'reference_answer', header: 'Reference Answer', aliases: ['referenceanswer', 'answer', 'modelanswer', 'idealanswer', 'expectedanswer'] },
    { key: 'key_points',       header: 'Key Points',       aliases: ['keypoints', 'points', 'markingpoints', 'rubric'] },
    { key: 'skill',            header: 'Skill',            aliases: ['skill', 'topic', 'technology'] },
    { key: 'sub_topic',        header: 'Sub Topic',        aliases: ['subtopic', 'subtopics'] },
    { key: 'question_angle',   header: 'Question Angle',   aliases: ['questionangle', 'angle'] },
    { key: 'difficulty',       header: 'Difficulty',       aliases: ['difficulty', 'level', 'diff'] },
  ],

  coding: [
    { key: 'category',                   header: 'Category',                   required: true, aliases: ['category', 'type', 'questiontype'] },
    { key: 'difficulty',                 header: 'Difficulty',                 aliases: ['difficulty', 'level', 'diff'] },
    { key: 'skill',                      header: 'Skill',                      aliases: ['skill', 'topic', 'technology'] },
    { key: 'language',                   header: 'Language',                   aliases: ['language', 'programminglanguage', 'lang'] },
    { key: 'problem_theme',              header: 'Problem Theme',              aliases: ['problemtheme', 'theme'] },
    { key: 'problem_statement',          header: 'Problem Statement',          aliases: ['problemstatement', 'statement', 'problem'] },
    { key: 'constraints',                header: 'Constraints',                aliases: ['constraints', 'constraint', 'limits'] },
    { key: 'input_format',               header: 'Input Format',               aliases: ['inputformat'] },
    { key: 'output_format',              header: 'Output Format',              aliases: ['outputformat'] },
    ...exampleColumns,
    { key: 'question',                   header: 'Question',                   aliases: ['question', 'q', 'ques'] },
    { key: 'expected_discussion_points', header: 'Expected Discussion Points', aliases: ['expecteddiscussionpoints', 'discussionpoints'] },
    { key: 'code_scenario',              header: 'Code Scenario',              aliases: ['codescenario', 'buggycode', 'code'] },
    { key: 'observed_behavior',          header: 'Observed Behavior',          aliases: ['observedbehavior', 'observedbehaviour', 'symptom'] },
    { key: 'scenario',                   header: 'Scenario',                   aliases: ['scenario', 'situation'] },
    { key: 'task',                       header: 'Task',                       aliases: ['task', 'todo'] },
    { key: 'expected_approach',          header: 'Expected Approach',          aliases: ['expectedapproach', 'approach', 'solution'] },
  ],
};

/** Lookup table: normalised header -> canonical key, per type. */
const ALIAS_INDEX = Object.fromEntries(
  TYPES.map((type) => {
    const idx = {};
    for (const col of COLUMNS[type]) {
      idx[normKey(col.header)] = col.key;
      for (const a of col.aliases || []) idx[normKey(a)] = col.key;
    }
    return [type, idx];
  })
);

// ─── Workbook parsing ─────────────────────────────────────────────────────────

/** Which import type does this sheet name refer to? */
const sheetType = (name) => {
  const n = normKey(name);
  for (const type of TYPES) {
    if (SHEET_ALIASES[type].some((a) => n === a || n.includes(a))) return type;
  }
  return null;
};

/**
 * Guess the type of an unnamed/oddly-named sheet from its headers.
 * Lets a plain CSV export work without the user renaming anything.
 */
const guessTypeFromHeaders = (headers) => {
  const keys = headers.map(normKey);
  const has = (k) => keys.includes(normKey(k));
  if (has('Option A') || has('Correct Answer')) return 'mcq';
  if (has('Problem Statement') || has('Code Scenario') || has('Category')) return 'coding';
  if (has('Reference Answer') || has('Key Points')) return 'subjective';
  return null;
};

/**
 * Find the row holding the headers. Users habitually paste a title or a blank
 * line above the table, which would otherwise shift every column by one row.
 * Scans the first 10 rows for the one with the most recognised headers.
 */
const findHeaderRow = (matrix, type) => {
  const idx = type ? ALIAS_INDEX[type] : null;
  let best = { row: -1, hits: 0 };
  const limit = Math.min(10, matrix.length);
  for (let r = 0; r < limit; r++) {
    const cells = (matrix[r] || []).map(normKey).filter(Boolean);
    if (!cells.length) continue;
    const hits = idx
      ? cells.filter((c) => idx[c]).length
      : cells.filter((c) => TYPES.some((t) => ALIAS_INDEX[t][c])).length;
    if (hits > best.hits) best = { row: r, hits };
  }
  return best.hits >= 2 ? best.row : -1;
};

/**
 * Read an .xlsx / .xls / .csv ArrayBuffer into per-type raw row objects.
 *
 * Returns { rows: { mcq: [], subjective: [], coding: [] }, notices: [], fatal: string|null }
 * Each row carries `__row` (1-based Excel row number) for error reporting.
 */
export const parseWorkbook = (arrayBuffer) => {
  const notices = [];
  const rows = { mcq: [], subjective: [], coding: [] };

  let wb;
  try {
    wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: true, cellText: false, cellNF: false });
  } catch (e) {
    return { rows, notices, fatal: `This file could not be read as a spreadsheet (${e.message}). Save it as .xlsx and try again.` };
  }

  if (!wb.SheetNames || wb.SheetNames.length === 0) {
    return { rows, notices, fatal: 'The workbook has no sheets.' };
  }

  const singleSheet = wb.SheetNames.length === 1;
  let matchedAny = false;

  for (const name of wb.SheetNames) {
    const ws = wb.Sheets[name];
    if (!ws) continue;

    // Read as a matrix first so we can locate the real header row.
    const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '', blankrows: false });
    if (!matrix.length) continue;

    let type = sheetType(name);
    const headerRow = findHeaderRow(matrix, type);

    if (headerRow === -1) {
      if (type) notices.push(`Sheet "${name}": no recognisable header row in the first 10 rows — skipped.`);
      continue;
    }

    if (!type) {
      type = guessTypeFromHeaders(matrix[headerRow] || []);
      if (type && singleSheet) {
        notices.push(`Sheet "${name}" isn't named MCQ / Theory / Coding — treated as ${TYPE_LABEL[type]} based on its columns.`);
      } else if (!type) {
        continue; // unrelated sheet (Instructions, Examples, Reference) — silently ignore
      }
    }

    matchedAny = true;

    const headers = (matrix[headerRow] || []).map((h) => clean(h));
    const idx = ALIAS_INDEX[type];

    const unknown = headers.filter((h) => h && !idx[normKey(h)]);
    if (unknown.length) {
      notices.push(`Sheet "${name}": ignoring unrecognised column${unknown.length > 1 ? 's' : ''} ${unknown.map((u) => `"${u}"`).join(', ')}.`);
    }

    for (let r = headerRow + 1; r < matrix.length; r++) {
      const cells = matrix[r] || [];
      const row = { __row: r + 1, __sheet: name };
      let hasData = false;

      headers.forEach((h, c) => {
        const key = idx[normKey(h)];
        if (!key) return;
        const val = clean(cells[c]);
        // Later duplicate columns must not blank out an earlier filled one.
        if (val || row[key] === undefined) row[key] = val;
        if (val) hasData = true;
      });

      if (hasData) rows[type].push(row);
    }
  }

  if (!matchedAny) {
    return {
      rows,
      notices,
      fatal: 'No MCQ, Theory or Coding sheet was found. Download the template and paste your questions into it.',
    };
  }

  return { rows, notices, fatal: null };
};

// ─── Correct-answer resolution (MCQ) ─────────────────────────────────────────

/**
 * The DB stores correct_answer as option TEXT (the UI matches by text), but
 * people naturally write "B" or "A, C" in a spreadsheet. Accept both.
 *
 * `positional` keeps blanks so that "C" still maps to the Option C column even
 * if Option B was left empty.
 */
export const resolveCorrectAnswers = (raw, positional) => {
  const filled = positional.filter(Boolean);
  const text = clean(raw);
  if (!text) return { resolved: [], unresolved: [] };

  // Whole-cell exact match first, so an answer containing a comma survives.
  const whole = filled.find((o) => o.toLowerCase() === text.toLowerCase());
  if (whole) return { resolved: [whole], unresolved: [] };

  const tokens = text.split(/[,;|\n]/).map((t) => clean(t)).filter(Boolean);
  const resolved = [];
  const unresolved = [];

  for (const t of tokens) {
    const letter = /^([A-Za-z])[).\].]?$/.exec(t);
    if (letter) {
      const i = letter[1].toUpperCase().charCodeAt(0) - 65;
      if (i >= 0 && i < positional.length && positional[i]) {
        resolved.push(positional[i]);
        continue;
      }
    }
    const exact = filled.find((o) => o.toLowerCase() === t.toLowerCase());
    if (exact) { resolved.push(exact); continue; }

    // Tolerate a trailing period or an "A) " prefix left on pasted text.
    const loose = filled.find((o) => o.toLowerCase().replace(/^[a-f][).]\s*/, '') === t.toLowerCase().replace(/^[a-f][).]\s*/, ''));
    if (loose) { resolved.push(loose); continue; }

    unresolved.push(t);
  }

  return { resolved: [...new Set(resolved)], unresolved };
};

// ─── Row -> DB document ───────────────────────────────────────────────────────

const truncNotice = (issues, row, field, value) => {
  if (value.length > MAX_FIELD_LEN) {
    issues.warnings.push({ row: row.__row, field, message: `Very long (${value.length} chars) — check the cell didn't absorb neighbouring content.` });
  }
};

/** Collect every missing required field into one error instead of one error per field. */
const reportMissing = (issues, row, missing, context) => {
  if (!missing.length) return false;
  issues.errors.push({
    row: row.__row,
    field: context,
    message: `Missing required ${missing.length === 1 ? 'field' : 'fields'}: ${missing.join(', ')}.`,
  });
  return true;
};

const buildMcq = (row, issues) => {
  const question = clean(row.question);
  const positional = Array.from({ length: MAX_OPTIONS }, (_, i) =>
    clean(row[`option${String.fromCharCode(65 + i)}`])
  );
  const options = positional.filter(Boolean);
  const rawDifficulty = clean(row.difficulty);
  const difficulty = normDifficulty(rawDifficulty);

  const missing = [];
  if (!question) missing.push('Question');
  if (options.length < MIN_OPTIONS) {
    missing.push(`${MIN_OPTIONS} options (only ${options.length} filled)`);
  }
  if (!clean(row.correct_answer)) missing.push('Correct Answer');
  if (!rawDifficulty) missing.push('Difficulty');

  if (reportMissing(issues, row, missing, 'MCQ')) return null;

  if (!difficulty) {
    issues.errors.push({
      row: row.__row,
      field: 'Difficulty',
      message: `"${rawDifficulty}" must be easy, medium or hard.`,
    });
    return null;
  }

  truncNotice(issues, row, 'Question', question);

  const lower = options.map((o) => o.toLowerCase());
  if (lower.some((o, i) => lower.indexOf(o) !== i)) {
    issues.warnings.push({ row: row.__row, field: 'Options', message: 'Two options have identical text — both will show as correct if either is the answer.' });
  }

  const { resolved, unresolved } = resolveCorrectAnswers(row.correct_answer, positional);
  if (unresolved.length) {
    issues.errors.push({ row: row.__row, field: 'Correct Answer', message: `${unresolved.map((u) => `"${u}"`).join(', ')} doesn't match any option letter or text.` });
    return null;
  }
  if (resolved.length === options.length) {
    issues.warnings.push({ row: row.__row, field: 'Correct Answer', message: 'Every option is marked correct.' });
  }

  return { question, options, correct_answer: resolved, difficulty };
};

const buildSubjective = (row, issues) => {
  const question    = clean(row.question);
  const skill       = clean(row.skill);
  const reference   = clean(row.reference_answer);
  const key_points  = splitList(row.key_points);
  const rawDifficulty = clean(row.difficulty);
  const difficulty  = normDifficulty(rawDifficulty);

  const missing = [];
  if (!question)          missing.push('Question');
  if (!skill)             missing.push('Skill');
  if (!rawDifficulty)     missing.push('Difficulty');
  if (!reference)         missing.push('Reference Answer');
  if (!key_points.length) missing.push('Key Points');

  if (reportMissing(issues, row, missing, 'Theory')) return null;

  if (!difficulty) {
    issues.errors.push({
      row: row.__row,
      field: 'Difficulty',
      message: `"${rawDifficulty}" must be easy, medium or hard.`,
    });
    return null;
  }

  truncNotice(issues, row, 'Question', question);
  truncNotice(issues, row, 'Reference Answer', reference);

  const angle = clean(row.question_angle);
  if (angle && !QUESTION_ANGLES.some((a) => a.toLowerCase() === angle.toLowerCase())) {
    issues.warnings.push({ row: row.__row, field: 'Question Angle', message: `"${angle}" isn't one of the listed angles — saved as-is.` });
  }
  if (key_points.length < 2) {
    issues.warnings.push({ row: row.__row, field: 'Key Points', message: 'Only one key point — graders usually need a few to score against.' });
  }

  return {
    question,
    sub_topic:        clean(row.sub_topic),
    question_angle:   angle,
    reference_answer: reference,
    key_points,
    skill,
    difficulty,
  };
};

/**
 * Required fields per coding category.
 * `common` applies to every category; the rest is what that category needs on top.
 * A blank Category is treated as "Coding", matching the schema default.
 */
const CODING_COMMON_REQUIRED = [
  ['difficulty', 'Difficulty'],
  ['skill',      'Skill'],
  ['language',   'Language'],
];

const CODING_REQUIRED = {
  Coding: [
    ['problem_statement', 'Problem Statement'],
    ['constraints',       'Constraints'],
    ['input_format',      'Input Format'],
    ['output_format',     'Output Format'],
  ],
  Conceptual: [['question', 'Question']],
  Debugging:  [['code_scenario', 'Code Scenario'], ['question', 'Question']],
  Tooling:    [['scenario', 'Scenario'], ['task', 'Task']],
};

const buildCoding = (row, issues) => {
  const rawCategory = clean(row.category);
  const category = rawCategory ? normCategory(rawCategory) : 'Coding';
  if (!category) {
    issues.errors.push({ row: row.__row, field: 'Category', message: `"${rawCategory}" must be one of ${CODING_CATEGORIES.join(', ')}.` });
    return null;
  }

  const examples = [];
  for (let n = 1; n <= MAX_EXAMPLES; n++) {
    const input       = clean(row[`example${n}_input`]);
    const output      = clean(row[`example${n}_output`]);
    const explanation = clean(row[`example${n}_explanation`]);
    if (input || output || explanation) examples.push({ input, output, explanation });
  }

  const missing = [...CODING_COMMON_REQUIRED, ...CODING_REQUIRED[category]]
    .filter(([key]) => !clean(row[key]))
    .map(([, label]) => label);

  if (category === 'Coding' && !examples.some((ex) => ex.input && ex.output)) {
    missing.push('Example 1 Input and Output');
  }

  if (reportMissing(issues, row, missing, category)) return null;

  const rawDifficulty = clean(row.difficulty);
  const difficulty = normDifficulty(rawDifficulty);
  if (!difficulty) {
    issues.errors.push({
      row: row.__row,
      field: 'Difficulty',
      message: `"${rawDifficulty}" must be easy, medium or hard.`,
    });
    return null;
  }

  const language = clean(row.language);
  const statement = clean(row.problem_statement);
  truncNotice(issues, row, 'Problem Statement', statement);

  return {
    category,
    difficulty,
    skill:                      clean(row.skill),
    problem_theme:              clean(row.problem_theme),
    language,
    programming_language:       language,
    problem_statement:          statement,
    constraints:                clean(row.constraints),
    input_format:               clean(row.input_format),
    output_format:              clean(row.output_format),
    examples,
    example_input:              '',
    expected_output:            '',
    expected_discussion_points: clean(row.expected_discussion_points),
    code_scenario:              clean(row.code_scenario),
    observed_behavior:          clean(row.observed_behavior),
    question:                   clean(row.question),
    scenario:                   clean(row.scenario),
    task:                       clean(row.task),
    expected_approach:          clean(row.expected_approach),
  };
};

const BUILDERS = { mcq: buildMcq, subjective: buildSubjective, coding: buildCoding };

/**
 * Validate and convert parsed rows into DB-shaped documents.
 *
 * @param {string} type              'mcq' | 'subjective' | 'coding'
 * @param {Array}  rows              output of parseWorkbook
 * @param {Array}  existingQuestions questions already on the JD, for dupe detection
 * @returns {{ valid: Array, errors: Array, warnings: Array, duplicates: Array }}
 */
export const validateRows = (type, rows, existingQuestions = []) => {
  const issues = { errors: [], warnings: [] };
  const valid = [];
  const duplicates = [];

  const seenInFile = new Map();
  const seenInDb = new Set(
    existingQuestions
      .map((q) => dupeKey(q?.question || q?.problem_statement || q?.scenario || q?.task || ''))
      .filter(Boolean)
  );

  for (const row of rows) {
    const doc = BUILDERS[type](row, issues);
    if (!doc) continue;

    const identity = dupeKey(doc.question || doc.problem_statement || doc.scenario || doc.task);

    if (identity && seenInDb.has(identity)) {
      duplicates.push({ row: row.__row, reason: 'already on this JD', preview: (doc.question || doc.problem_statement || doc.scenario || doc.task).slice(0, 90) });
      continue;
    }
    if (identity && seenInFile.has(identity)) {
      duplicates.push({ row: row.__row, reason: `same as row ${seenInFile.get(identity)}`, preview: (doc.question || doc.problem_statement || doc.scenario || doc.task).slice(0, 90) });
      continue;
    }
    if (identity) seenInFile.set(identity, row.__row);

    valid.push(doc);
  }

  return { valid, errors: issues.errors, warnings: issues.warnings, duplicates };
};

// ─── Template workbook ────────────────────────────────────────────────────────

const INSTRUCTIONS = [
  ['Bulk question import — how to fill this file'],
  [],
  ['1. Put each question on its own row, in the sheet that matches its type: MCQ, Theory or Coding.'],
  ['2. Do not rename, reorder or delete the header row. Extra columns you add are ignored.'],
  ['3. Leave a sheet empty if you have nothing of that type. Empty rows are skipped.'],
  ['4. Every column marked "required" below must be filled. A row missing one is reported and not imported.'],
  ['5. Open the "Examples" sheet to see a filled-in row for each type. That sheet is never imported.'],
  [`6. Maximum ${MAX_ROWS} questions per file. Split larger sets into several files.`],
  [],
  ['MCQ sheet'],
  ['Question', 'required', 'The question text.'],
  ['Option A – Option D', 'required', `At least ${MIN_OPTIONS} options must be filled. Option E and F are optional extras.`],
  ['Correct Answer', 'required', 'Option letter (B), several letters (A, C), or the full option text.'],
  ['Difficulty', 'required', 'easy, medium or hard. Anything else is rejected.'],
  [],
  ['Theory sheet'],
  ['Question', 'required', 'The question text.'],
  ['Skill', 'required', 'e.g. RTOS Concepts, React, SQL.'],
  ['Difficulty', 'required', 'easy, medium or hard.'],
  ['Reference Answer', 'required', 'The model answer graders compare against.'],
  ['Key Points', 'required', 'At least one. One point per line (Alt+Enter inside the cell) or separated by | .'],
  ['Sub Topic', 'optional', 'e.g. pointer arithmetic and type casting.'],
  ['Question Angle', 'optional', QUESTION_ANGLES.join(' / ')],
  [],
  ['Coding sheet — required for every row'],
  ['Difficulty', 'required', 'easy, medium or hard.'],
  ['Skill', 'required', 'e.g. C/C++, RTOS, Linux Environment.'],
  ['Language', 'required', 'e.g. C, Python, Java.'],
  [],
  ['Coding sheet — also required, depending on Category'],
  ['Category', 'optional', CODING_CATEGORIES.join(' / ') + '. Left blank it means Coding.'],
  ['If Category = Coding', 'required', 'Problem Statement, Constraints, Input Format, Output Format, and Example 1 Input + Output.'],
  ['If Category = Conceptual', 'required', 'Question.'],
  ['If Category = Debugging', 'required', 'Code Scenario and Question.'],
  ['If Category = Tooling', 'required', 'Scenario and Task.'],
  ['Example 2–3', 'optional', 'Extra examples. Leave all three cells of a group blank to skip it.'],
  ['Problem Theme, Observed Behavior, Expected Approach, Expected Discussion Points', 'optional', 'Free text.'],
  [],
  ['What happens after you choose the file'],
  ['Every row is checked before anything is saved. You will see how many rows are ready,'],
  ['which rows have problems and why, and which rows are duplicates. Nothing is written to'],
  ['the question bank until you confirm the import.'],
];

const EXAMPLE_ROWS = {
  mcq: {
    Question: 'Which HTTP status code indicates that a request succeeded and a new resource was created?',
    'Option A': '200 OK',
    'Option B': '201 Created',
    'Option C': '204 No Content',
    'Option D': '301 Moved Permanently',
    'Correct Answer': 'B',
    Difficulty: 'easy',
  },
  subjective: {
    Question: 'Explain why a React component re-renders when its parent re-renders, even if its props have not changed.',
    'Reference Answer': 'React re-renders the whole subtree by default. A parent re-render creates new element objects for its children, so React reconciles them regardless of prop equality. React.memo opts a component out by shallow-comparing props, but new object or function props defeat it unless they are memoised.',
    'Key Points': 'Default reconciliation walks the subtree\nNew element objects are created each render\nReact.memo does a shallow prop comparison\nInline objects and callbacks break memoisation',
    Skill: 'React',
    'Sub Topic': 'rendering and reconciliation',
    'Question Angle': 'explain how/why',
    Difficulty: 'medium',
  },
  coding: {
    Category: 'Coding',
    Difficulty: 'medium',
    Skill: 'Arrays',
    Language: 'Python',
    'Problem Theme': 'prefix sums',
    'Problem Statement': 'Given an array of integers and a target sum, return the number of contiguous subarrays whose elements add up to the target.',
    Constraints: '1 <= n <= 10^5, -10^4 <= arr[i] <= 10^4',
    'Input Format': 'First line: n and target. Second line: n space-separated integers.',
    'Output Format': 'A single integer: the count of matching subarrays.',
    'Example 1 Input': '5 7\n3 4 7 2 -3',
    'Example 1 Output': '2',
    'Example 1 Explanation': 'The subarrays [3,4] and [7] both sum to 7.',
  },
};

const autoWidth = (rowsOfArrays, min = 10, max = 60) => {
  const widths = [];
  for (const row of rowsOfArrays) {
    row.forEach((cell, i) => {
      const len = String(cell ?? '').split('\n')[0].length;
      widths[i] = Math.max(widths[i] || min, Math.min(max, len + 2));
    });
  }
  return widths.map((w) => ({ wch: w }));
};

/** Build and trigger a download of the import template. */
export const downloadTemplate = (filename = 'question_import_template.xlsx') => {
  const wb = XLSX.utils.book_new();

  const wsInstr = XLSX.utils.aoa_to_sheet(INSTRUCTIONS);
  wsInstr['!cols'] = [{ wch: 44 }, { wch: 14 }, { wch: 86 }];
  XLSX.utils.book_append_sheet(wb, wsInstr, 'Instructions');

  // Data sheets: headers only. Samples live on their own sheet so nobody
  // imports the sample row by forgetting to delete it.
  for (const type of TYPES) {
    const headers = COLUMNS[type].map((c) => c.header);
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    ws['!cols'] = autoWidth([headers], 14, 34);
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, ws, TYPE_LABEL[type]);
  }

  const exampleAoa = [];
  for (const type of TYPES) {
    const headers = COLUMNS[type].map((c) => c.header);
    exampleAoa.push([`${TYPE_LABEL[type]} — example row (this sheet is never imported)`]);
    exampleAoa.push(headers);
    exampleAoa.push(headers.map((h) => EXAMPLE_ROWS[type][h] ?? ''));
    exampleAoa.push([]);
  }
  const wsEx = XLSX.utils.aoa_to_sheet(exampleAoa);
  wsEx['!cols'] = autoWidth(exampleAoa, 14, 50);
  XLSX.utils.book_append_sheet(wb, wsEx, 'Examples');

  const reference = [
    ['Allowed values'],
    [],
    ['Difficulty', ...DIFFICULTIES],
    ['Category (Coding sheet)', ...CODING_CATEGORIES],
    ['Question Angle (Theory sheet)', ...QUESTION_ANGLES],
    [],
    ['Correct Answer accepts'],
    ['A single letter', 'B'],
    ['Several letters', 'A, C'],
    ['The full option text', '201 Created'],
  ];
  const wsRef = XLSX.utils.aoa_to_sheet(reference);
  wsRef['!cols'] = autoWidth(reference, 16, 34);
  XLSX.utils.book_append_sheet(wb, wsRef, 'Reference');

  XLSX.writeFile(wb, filename);
};